import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import RouteGuard from "components/RouteGuard";
const AdminPanelDashboard = lazy(() => import('./pages/admin-panel-dashboard'));
const AnalyticsDashboard = lazy(() => import('./pages/analytics-dashboard'));
import AIChatInterface from './pages/ai-chat-interface-dai-mentor';
import ActivityInterface from './pages/activity-interface';
import LoginScreen from './pages/login-screen';
import RegistrationScreen from './pages/registration-screen';
import ForgotPasswordScreen from './pages/forgot-password';
import ResetPasswordScreen from './pages/reset-password';
import UserProgressDashboard from './pages/user-progress-dashboard';
import CourseDetailView from './pages/course-detail-view';
import CourseModulesView from './pages/course-modules-view';
import DashboardHome from './pages/dashboard-home';
import VideoPlayerInterface from './pages/video-player-interface';
const ThemeSettingsPanel = lazy(() => import('./pages/theme-settings-panel'));
const AdminCourseManagement = lazy(() => import('./pages/admin-course-management'));
import StudentLessonPage from './pages/student-lesson-page';
const UserManagementInterface = lazy(() => import('./pages/user-management-interface'));
const EnrollmentManagementInterface = lazy(() => import('./pages/enrollment-management-interface'));
const ContentManagementInterface = lazy(() => import('./pages/content-management-interface'));
const MediaLibraryManagement = lazy(() => import('./pages/media-library-management'));
const CertificateManagementInterface = lazy(() => import('./pages/certificate-management-interface'));
const PaymentManagementInterface = lazy(() => import('./pages/payment-management-interface'));
const AffiliateManagementInterface = lazy(() => import('./pages/affiliate-management-interface'));
const CommunicationCampaignManagement = lazy(() => import('./pages/communication-campaign-management'));
const SystemAdministrationInterface = lazy(() => import('./pages/system-administration-interface'));
const CampaignCreationForm = lazy(() => import('./pages/campaign-creation-form'));
const EmailTemplateEditor = lazy(() => import('./pages/email-template-editor'));
const CampaignCreationWizard = lazy(() => import('./pages/campaign-creation-wizard'));
const EmailTemplateBuilder = lazy(() => import('./pages/email-template-builder'));
import MediaMappingConfiguration from './pages/media-mapping-configuration';
import AffiliateRegistrationForm from './pages/affiliate-registration-form';
import ContentModuleEditor from './pages/content-module-editor';
import SystemBackupConfiguration from './pages/system-backup-configuration';
import InviteAccept from './pages/invite-accept';
import DebugData from './pages/debug-data';
import CoursesCatalog from './pages/courses-catalog';
import GoalsMapInterface from './pages/goals-map-interface';
const LessonReviewQueue = lazy(() => import('./pages/lesson-review-queue'));
const LearningOperations = lazy(() => import('./pages/learning-operations'));
// Novas páginas de calendário
import AppointmentRequestModal from './pages/appointment-request-modal';
import AvailabilityConfiguration from './pages/availability-configuration';
import CalendarMainInterface from './pages/calendar-main-interface';
const RequestManagementInterface = lazy(() => import('./pages/request-management-interface'));
import UserCalendarView from './pages/user-calendar-view';


const Routes = () => {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<div className="mh-admin-loading" role="status">Carregando…</div>}>
      <RouterRoutes>
        {/* Rotas Públicas (sem autenticação) */}
        <Route path="/login-screen" element={<LoginScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/registration-screen" element={<RegistrationScreen />} />

        {/* Rotas Protegidas (requerem autenticação) */}
        <Route path="/" element={
          <RouteGuard requireAuth={true}>
            <Navigate to="/dashboard-home" replace />
          </RouteGuard>
        } />
        
        <Route path="/admin-panel-dashboard" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <AdminPanelDashboard />
          </RouteGuard>
        } />
        
        <Route path="/admin/analytics" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <AnalyticsDashboard />
          </RouteGuard>
        } />

        <Route path="/admin/learning-operations" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <LearningOperations />
          </RouteGuard>
        } />
        
        <Route path="/ai-chat-interface-dai-mentor" element={
          <RouteGuard requireAuth={true}>
            <AIChatInterface />
          </RouteGuard>
        } />
        
        <Route path="/activity-interface" element={
          <RouteGuard requireAuth={true}>
            <ActivityInterface />
          </RouteGuard>
        } />
        
        <Route path="/user-progress-dashboard" element={
          <RouteGuard requireAuth={true}>
            <UserProgressDashboard />
          </RouteGuard>
        } />
        
        <Route path="/course-detail-view" element={
          <RouteGuard requireAuth={true}>
            <CourseDetailView />
          </RouteGuard>
        } />
        
        <Route path="/course/:courseSlug" element={
          <RouteGuard requireAuth={true}>
            <CourseDetailView />
          </RouteGuard>
        } />
        
        <Route path="/course/:courseSlug/modules" element={
          <RouteGuard requireAuth={true}>
            <CourseModulesView />
          </RouteGuard>
        } />
        
        <Route path="/course/:courseSlug/module/:moduleSlug" element={
          <RouteGuard requireAuth={true}>
            <CourseDetailView />
          </RouteGuard>
        } />
        
        <Route path="/courses" element={
          <RouteGuard requireAuth={true}>
            <CoursesCatalog />
          </RouteGuard>
        } />
        
        <Route path="/dashboard-home" element={
          <RouteGuard requireAuth={true}>
            <DashboardHome />
          </RouteGuard>
        } />
        
        <Route path="/video-player-interface" element={
          <RouteGuard requireAuth={true}>
            <VideoPlayerInterface />
          </RouteGuard>
        } />
        
        <Route path="/lesson/:lessonId" element={
          <RouteGuard requireAuth={true}>
            <VideoPlayerInterface />
          </RouteGuard>
        } />
        
        <Route path="/course/:courseSlug/module/:moduleSlug/lesson/:lessonId" element={
          <RouteGuard requireAuth={true}>
            <VideoPlayerInterface />
          </RouteGuard>
        } />
        
        <Route path="/theme-settings-panel" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ThemeSettingsPanel />
          </RouteGuard>
        } />
        
        <Route path="/admin/settings/theme-white-label" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ThemeSettingsPanel />
          </RouteGuard>
        } />
        
        <Route path="/admin-course-management" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <AdminCourseManagement />
          </RouteGuard>
        } />
        
        <Route path="/admin/courses" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <AdminCourseManagement />
          </RouteGuard>
        } />
        
        <Route path="/student-lesson-page" element={
          <RouteGuard requireAuth={true}>
            <StudentLessonPage />
          </RouteGuard>
        } />
        
        <Route path="/user-management-interface" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <UserManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/content-management-interface" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ContentManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/modules" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ContentManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/lessons" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ContentManagementInterface />
          </RouteGuard>
        } />

        <Route path="/admin/lesson-reviews" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <LessonReviewQueue />
          </RouteGuard>
        } />
        
        <Route path="/admin/activities" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ContentManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/library" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ContentManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/media-library-management" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <MediaLibraryManagement />
          </RouteGuard>
        } />
        
        <Route path="/certificate-management-interface" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <CertificateManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/payment-management-interface" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <PaymentManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/affiliate-management-interface" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <AffiliateManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/affiliates" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <AffiliateManagementInterface />
          </RouteGuard>
        } />
        
        {/* TODO: Criar página AffiliateReportsInterface
        <Route path="/admin/affiliates/reports" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <AffiliateReportsInterface />
          </RouteGuard>
        } />
        */}
        
        {/* TODO: Criar página AffiliateSettingsInterface
        <Route path="/admin/affiliates/settings" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <AffiliateSettingsInterface />
          </RouteGuard>
        } />
        */}
        
        <Route path="/communication-campaign-management" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <CommunicationCampaignManagement />
          </RouteGuard>
        } />
        
        <Route path="/admin/comms/campaigns" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <CommunicationCampaignManagement />
          </RouteGuard>
        } />
        
        {/* TODO: Criar página CommunicationSendsInterface
        <Route path="/admin/comms/sends" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <CommunicationSendsInterface />
          </RouteGuard>
        } />
        */}
        
        <Route path="/system-administration-interface" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin']}>
            <SystemAdministrationInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/system/logs" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin']}>
            <SystemAdministrationInterface />
          </RouteGuard>
        } />
        
        {/* TODO: Criar página SystemIntegrationsInterface
        <Route path="/admin/system/integrations" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <SystemIntegrationsInterface />
          </RouteGuard>
        } />
        */}
        
        <Route path="/campaign-creation-form" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <CampaignCreationForm />
          </RouteGuard>
        } />
        
        <Route path="/email-template-editor" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <EmailTemplateEditor />
          </RouteGuard>
        } />

        {/* New Enhanced Routes */}
        <Route path="/campaign-creation-wizard" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <CampaignCreationWizard />
          </RouteGuard>
        } />
        
        <Route path="/email-template-builder" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <EmailTemplateBuilder />
          </RouteGuard>
        } />
        
        <Route path="/admin/comms/templates" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <EmailTemplateBuilder />
          </RouteGuard>
        } />

        {/* Goals Map Interface */}
        <Route path="/goals-map-interface" element={
          <RouteGuard requireAuth={true}>
            <GoalsMapInterface />
          </RouteGuard>
        } />
        
        <Route path="/mapa-de-metas" element={
          <RouteGuard requireAuth={true}>
            <GoalsMapInterface />
          </RouteGuard>
        } />

        {/* Media Mapping Configuration */}
        <Route path="/media-mapping-configuration" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <MediaMappingConfiguration />
          </RouteGuard>
        } />
        
        <Route path="/admin/settings/media-mapping" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <MediaMappingConfiguration />
          </RouteGuard>
        } />
        
        {/* TODO: Criar página MediaPlayerSettingsInterface
        <Route path="/admin/settings/media-player" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <MediaPlayerSettingsInterface />
          </RouteGuard>
        } />
        */}
        
        {/* TODO: Criar página EmailSettingsInterface
        <Route path="/admin/settings/email" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <EmailSettingsInterface />
          </RouteGuard>
        } />
        */}
        
        {/* TODO: Criar página LearningReportsInterface
        <Route path="/admin/reports/learning" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <LearningReportsInterface />
          </RouteGuard>
        } />
        */}

        {/* New Routes for Academy Platform Extension */}
        <Route path="/affiliate-registration-form" element={
          <RouteGuard requireAuth={true}>
            <AffiliateRegistrationForm />
          </RouteGuard>
        } />
        
        <Route path="/content-module-editor" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <ContentModuleEditor />
          </RouteGuard>
        } />

        {/* System Backup Configuration */}
        <Route path="/system-backup-configuration" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin']}>
            <SystemBackupConfiguration />
          </RouteGuard>
        } />
        
        <Route path="/admin/system/backup" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin']}>
            <SystemBackupConfiguration />
          </RouteGuard>
        } />

        {/* User Management Routes */}
        <Route path="/admin/users" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <UserManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/users/new" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <UserManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/users/:id" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <UserManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/students" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <UserManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/instructors" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <UserManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/admin/admins" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin']}>
            <UserManagementInterface />
          </RouteGuard>
        } />

        <Route path="/admin/enrollments" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager', 'owner']}>
            <EnrollmentManagementInterface />
          </RouteGuard>
        } />

        {/* Invite Accept Route */}
        <Route path="/invite/:token" element={
          <RouteGuard requireAuth={true}>
            <InviteAccept />
          </RouteGuard>
        } />

        {/* Debug Data Route */}
        <Route path="/debug/data" element={
          <RouteGuard requireAuth={true}>
            <DebugData />
          </RouteGuard>
        } />

        {/* Novas Rotas de Calendário */}
        <Route path="/appointment-request-modal" element={
          <RouteGuard requireAuth={true}>
            <AppointmentRequestModal />
          </RouteGuard>
        } />
        
        <Route path="/availability-configuration" element={
          <RouteGuard requireAuth={true}>
            <AvailabilityConfiguration />
          </RouteGuard>
        } />
        
        <Route path="/calendar-main-interface" element={
          <RouteGuard requireAuth={true}>
            <CalendarMainInterface />
          </RouteGuard>
        } />
        
        <Route path="/request-management-interface" element={
          <RouteGuard requireAuth={true} requiredRoles={['admin', 'manager']}>
            <RequestManagementInterface />
          </RouteGuard>
        } />
        
        <Route path="/user-calendar-view" element={
          <RouteGuard requireAuth={true}>
            <UserCalendarView />
          </RouteGuard>
        } />

        {/* 404 Route - Protegida também */}
        <Route path="*" element={
          <RouteGuard requireAuth={true}>
            <NotFound />
          </RouteGuard>
        } />
      </RouterRoutes>
      </Suspense>
      </ErrorBoundary>
  );
};

export default Routes;
